import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, SlidersHorizontal } from "lucide-react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  selectNotifiesDeparmentsOptions,
  selectNotifiesStatesOptions,
} from "../../../app/reducers/book/notifies"
import { TAB_FILTER_FIELDS, CHAT_TABS, buildStandardThemes } from "./constants"
import MultiSelect from "./MultiSelect"

const BASE_STATUSES = [
  { key: "read", value: "read" },
  { key: "unread", value: "unread" },
]

const SimpleSelect = ({
  label,
  options,
  value,
  onChange,
  includeAllOption = true,
  allLabel = "Всі",
}) => {
  const computedOptions = includeAllOption ? [{ key: "", value: allLabel }, ...options] : options
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <div className="relative">
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm text-slate-700 appearance-none focus:outline-none focus:ring-2"
        >
          {computedOptions.map((o) => (
            <option key={o.key} value={o.key}>
              {o.value}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  )
}

export const FilterTrigger = ({ onClick, activeCount }) => (
  <button
    onClick={onClick}
    className="relative p-2 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
    title="Фільтри"
  >
    <SlidersHorizontal className="w-5 h-5 text-slate-600" />
    {activeCount > 0 && (
      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
        <span className="text-[9px] font-bold text-white">{activeCount}</span>
      </span>
    )}
  </button>
)

const useFieldRenderers = ({
  filters,
  onChange,
  t,
  departamentsOptions,
  statesOptions,
  messageThemeOptions,
  isAdmin,
}) => {
  const status_options = BASE_STATUSES.map((item) => ({
    key: item.key,
    value: item.key === "read" ? t("admin.read_messages") : t("admin.unread"),
  }))

  return {
    state_id: (
      <SimpleSelect
        key="state_id"
        label={t("admin.country")}
        allLabel={t("trainees.all_states")}
        options={statesOptions}
        value={filters.state_id || ""}
        onChange={(v) => onChange("state_id", v ? Number(v) : 0)}
      />
    ),

    departament_id: (
      <MultiSelect
        key="departament_id"
        label={t("admin.departament")}
        allLabel={t("admin.all")}
        options={departamentsOptions.map((o) => ({ ...o, key: String(o.key) }))}
        value={(filters.departament_ids || []).map(String)}
        onChange={(v) => onChange("departament_ids", v.map(Number))}
      />
    ),

    message_theme: (
      <MultiSelect
        key="message_theme"
        label={t("admin.message_subject", "Тема")}
        allLabel={t("admin.all")}
        options={messageThemeOptions}
        value={(filters.message_theme || []).map(String)}
        onChange={(v) => onChange("message_theme", v)}
      />
    ),

    status: (
      <SimpleSelect
        key="status"
        label={t("requests.status")}
        allLabel={t("admin.all")}
        options={status_options}
        value={filters.status || ""}
        onChange={(v) => onChange("status", v)}
      />
    ),

    date_range: (
      <div key="date_range" className="flex flex-col gap-1">
        <label className="text-xs font-medium text-slate-600">{t("admin.date", "Дата")}</label>
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={filters.date_from || ""}
            onChange={(e) => onChange("date_from", e.target.value || null)}
            className="flex-1 min-w-0 px-2 py-2 border border-slate-300 rounded-lg bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          <span className="text-slate-400 text-xs flex-shrink-0">—</span>
          <input
            type="date"
            value={filters.date_to || ""}
            onChange={(e) => onChange("date_to", e.target.value || null)}
            className="flex-1 min-w-0 px-2 py-2 border border-slate-300 rounded-lg bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
      </div>
    ),

    search: null,
  }
}

export const countActiveFilters = (filters, activeTab) => {
  const fields = TAB_FILTER_FIELDS[activeTab] ?? TAB_FILTER_FIELDS[CHAT_TABS.ALL]
  let n = 0
  if (fields.includes("status") && filters.status !== "") n++
  if (fields.includes("state_id") && filters.state_id > 0) n++
  if (fields.includes("departament_id") && filters.departament_ids?.length > 0) n++
  if (fields.includes("date_range") && (filters.date_from || filters.date_to)) n++
  if (fields.includes("search") && filters.search) n++
  if (fields.includes("company_id") && filters.company_id > 0) n++
  if (fields.includes("moderator_id") && filters.moderator_id > 0) n++
  if (fields.includes("bundle_type") && filters.bundle_type) n++
  if (fields.includes("message_theme") && filters.message_theme?.length > 0) n++
  return n
}

const FilterPanel = ({
  isOpen,
  onClose,
  filters,
  onChange,
  onSearch,
  onReset,
  activeTab,
  departments,
  messagethemes,
  isAdmin,
}) => {
  const { t } = useTranslation()
  const departamentsOptions = useSelector(selectNotifiesDeparmentsOptions)
  const statesOptions = useSelector(selectNotifiesStatesOptions)

  const messageThemeOptions = React.useMemo(() => {
    const standard = buildStandardThemes(t, isAdmin).map((s) => ({
      key: String(s.id),
      value: s.title,
    }))
    const custom = (messagethemes || []).map((s) => ({
      key: String(s.id),
      value: s.title,
    }))
    return custom.length > 0 ? [...custom, ...standard] : standard
  }, [messagethemes, isAdmin, t])

  const baseFields = TAB_FILTER_FIELDS[activeTab] ?? TAB_FILTER_FIELDS[CHAT_TABS.ALL]
  const visibleFields = React.useMemo(() => {
    return baseFields.filter((field) => {
      if (field === "departament_id") return departamentsOptions?.length > 0
      if (field === "message_theme") return activeTab === CHAT_TABS.ALL
      return true
    })
  }, [baseFields, departamentsOptions, activeTab])

  const fieldRenderers = useFieldRenderers({
    filters,
    onChange,
    t,
    departamentsOptions,
    statesOptions,
    messageThemeOptions,
    isAdmin,
  })

  const hasActiveFilters = countActiveFilters(filters, activeTab) > 0

  const handleApply = () => {
    onSearch()
    onClose()
  }
  const handleReset = () => {
    onReset()
    onClose()
  }

  const gridFields = visibleFields.filter((f) => f !== "search")

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="absolute inset-0 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="absolute top-0 left-0 bottom-0 z-20 bg-white shadow-xl flex flex-col overflow-hidden"
            style={{ width: "100%" }}
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-5 h-5 text-slate-600" />
                <h3 className="font-semibold text-slate-900">Фільтри</h3>
                {hasActiveFilters && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-medium rounded-full">
                    {t("admin.active2")}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {gridFields.map((field, i) => (
                <div key={i} className="grid gap-3 grid-cols-1">
                  {fieldRenderers[field] ?? null}
                </div>
              ))}

              {visibleFields.includes("search") && (
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    placeholder={t("admin.search_placeholder", "Пошук...")}
                    value={filters.search || ""}
                    onChange={(e) => onChange("search", e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleApply()
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm text-slate-700 focus:outline-none focus:ring-2"
                  />
                  <button
                    onClick={handleApply}
                    className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {t("requests.search")}
                  </button>
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex gap-2 flex-shrink-0">
              <button
                onClick={handleReset}
                className="flex-1 py-2 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors"
              >
                {t("admin.reset")}
              </button>
              <button
                onClick={handleApply}
                className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {t("admin.apply")}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default FilterPanel
